/** Splits CSV text into rows of cells (quotes, commas and new lines inside quotes handled). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell: string = '';
  let quoted: boolean = false;
  const src: string = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const ch: string = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') {
        i++;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter(r => r.some(c => c.trim()));
}

export interface IImportedClient {
  name: string;
  otherNames?: string;
  birthDate?: string;
  email?: string;
  phone?: string;
  number?: string;
}

const HEADERS: { [field in keyof IImportedClient]: RegExp } = {
  name: /^(full )?name$|^client( name)?$/i,
  otherNames: /other names?|aliases?|also known/i,
  birthDate: /birth|dob/i,
  email: /e-?mail/i,
  phone: /phone|mobile|cell/i,
  number: /client (no|number|#)|file (no|number|#)|^number$|^id$/i
};

/**
 * Clients from a spreadsheet saved as CSV, with a header row. Columns are recognised by their
 * names ("Name", "Date of birth", "Email", "Client number"…). Dates must be YYYY-MM-DD. Rows
 * without a name are skipped; problems come back in plain words, per row.
 */
export function clientsFromCsv(text: string): { clients: IImportedClient[]; problems: string[] } {
  const rows: string[][] = parseCsv(text);
  if (!rows.length) {
    return { clients: [], problems: ['The file is empty.'] };
  }
  const header: string[] = rows[0].map(h => h.trim());
  const col: { [field: string]: number } = {};
  for (const field of Object.keys(HEADERS) as (keyof IImportedClient)[]) {
    col[field] = header.findIndex(h => HEADERS[field]!.test(h));
  }
  if (col.name === -1) {
    return { clients: [], problems: ['There\'s no "Name" column in the first row.'] };
  }
  const clients: IImportedClient[] = [];
  const problems: string[] = [];
  rows.slice(1).forEach((r, i) => {
    const get = (field: string): string | undefined => (col[field] >= 0 ? (r[col[field]] ?? '').trim() || undefined : undefined);
    const name: string | undefined = get('name');
    if (!name) {
      problems.push(`Row ${i + 2}: no name, skipped.`);
      return;
    }
    let birthDate: string | undefined = get('birthDate');
    if (birthDate && !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      problems.push(`Row ${i + 2} (${name}): date of birth "${birthDate}" isn't YYYY-MM-DD, left empty.`);
      birthDate = undefined;
    }
    clients.push({ name, otherNames: get('otherNames'), birthDate, email: get('email'), phone: get('phone'), number: get('number') });
  });
  return { clients, problems };
}
